import * as React from 'react';

const FOCUSABLE =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const useFocusTrap = <T extends HTMLElement>(
	active: boolean,
	onEscape?: () => void
): React.RefObject<T> => {
	const containerRef = React.useRef<T>(null);
	const onEscapeRef = React.useRef(onEscape);
	onEscapeRef.current = onEscape;

	React.useEffect(() => {
		const container = containerRef.current;
		if (!active || !container) return undefined;

		const previouslyFocused = document.activeElement as HTMLElement | null;
		const getFocusable = (): HTMLElement[] =>
			Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));

		(getFocusable()[0] ?? container).focus();

		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.key === 'Escape' && onEscapeRef.current) {
				event.stopPropagation();
				onEscapeRef.current();
				return;
			}
			if (event.key !== 'Tab') return;
			const focusable = getFocusable();
			if (focusable.length === 0) {
				event.preventDefault();
				return;
			}
			const first = focusable[0];
			const last = focusable[focusable.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		container.addEventListener('keydown', onKeyDown);
		return () => {
			container.removeEventListener('keydown', onKeyDown);
			previouslyFocused?.focus?.();
		};
	}, [active]);

	return containerRef;
};
